import usePress from 'hooks/usePress'
import useHover from 'hooks/useHover'
import { Post } from '@wellesley/openapi'
import { Heart } from 'lucide-react'
import formatNumber from 'helpers/formatNumber'
import useActionGuard from 'hooks/useActionGuard'
import { cn, useIsMobile } from '@wellesley/ui'
import { FC, useCallback, ReactNode } from 'react'
import { ActivityTabs, getPostActivityRoute } from 'helpers/routes'

import {
  feedsApi,
  usePostApiV1PostsByPostIdLikesMutation,
  useDeleteApiV1PostsByPostIdLikesMutation,
} from '../../../api'
import PopupContent, {
  MAX_AVATARS,
  POPUP_OPEN_DELAY,
  POPUP_CLOSE_DELAY,
} from '../Popup'

type LikeButtonProps = {
  feed: Post
  isDisabled: boolean
  customContent?: ReactNode
  readonly?: boolean
}

const LikeButton: FC<LikeButtonProps> = ({
  feed,
  isDisabled,
  customContent,
  readonly,
}) => {
  const { id: feedId, liked, author: feedUser } = feed
  const counter = feed.stats.likes || 0
  const hasLikes = counter > 0

  const isMobile = useIsMobile()

  const [, isHovered, , handleMouseLeave, handleMouseEnter] =
    useHover<HTMLDivElement>({
      delay: POPUP_OPEN_DELAY,
      leaveDelay: POPUP_CLOSE_DELAY,
      useRefElement: false,
    })

  const [isPressed, touchEvents] = usePress(POPUP_OPEN_DELAY, POPUP_CLOSE_DELAY)

  const [like] = usePostApiV1PostsByPostIdLikesMutation()
  const [disLike] = useDeleteApiV1PostsByPostIdLikesMutation()

  const { guardedAction, ActionTooltipWrapper } = useActionGuard()

  const handleLike = useCallback(
    guardedAction(() => {
      if (liked) void disLike({ postId: feedId })
      else void like({ postId: feedId })
    }),
    [liked, feedId, disLike, like],
  )

  const canShowPopup = (isHovered || isPressed) && hasLikes

  const { data: likes, isLoading: isLoadingLikes } =
    feedsApi.useGetApiV1PostsByPostIdLikesQuery(
      {
        postId: feedId,
        limit: MAX_AVATARS,
      },
      { skip: !canShowPopup },
    )

  const likedUsers = likes?.map(({ user }) => user) || []

  return (
    <ActionTooltipWrapper>
      <div
        data-testid="like-button"
        onClick={readonly || isDisabled ? undefined : handleLike}
        className={cn('relative flex items-center', {
          'cursor-pointer': !isDisabled,
        })}
        {...(isMobile
          ? touchEvents
          : {
              onMouseEnter: handleMouseEnter,
              onMouseLeave: handleMouseLeave,
            })}
      >
        {canShowPopup && (
          <PopupContent
            users={likedUsers}
            counter={counter}
            isLoading={isLoadingLikes}
            actionButtonLink={getPostActivityRoute(
              feedUser,
              feedId,
              ActivityTabs.likes,
            )}
          />
        )}
        {customContent || (
          <>
            <Heart
              size={20}
              fill={liked && !canShowPopup ? 'var(--red-500)' : 'transparent'}
              color={
                liked || canShowPopup ? 'var(--red-500)' : 'var(--gray-600)'
              }
            />
            <span className="ml-xxxs text-sm font-medium">
              {formatNumber(counter)}
            </span>
          </>
        )}
      </div>
    </ActionTooltipWrapper>
  )
}

export default LikeButton
